import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { TableTopPanel } from 'components/TableTopPanel';
import { AG_GRID_ICON_SETUP } from 'constantsProject';
import { BimChapterEnum, ProjectStatusEnum } from 'gql/graphql';
import type { WorkOrderStatusEnum, ContractStatusEnum, BimActKc2VersionOtherItemData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { getRowClass } from 'pages/Project/BimProject/BimCommonComponents/WorkOrderPriceCellRenderers/WorkOrderOtherCellRenderers/';

import type { AgGridReact } from 'ag-grid-react';
import type { RefObject } from 'react';
import type React from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { BimWorkOrderPriceRowDetails } from '../../BimCommonComponents/BimWorkOrderPriceRowDetails';
import { BimWorkOrderOtherActKc2Details } from '../../WorkOrder/BimWorkOrderOtherActsKc2';
import { BimWorkOrderRowTypeEnum } from '../../WorkOrder/types';
import { useBimWorkOrderVersionTotalsRow } from '../BimWorkOrderPriceVersionTable/hooks/useBimWorkOrderVersionTotalsRow';
import { useBimWorkOrderVersionPriceStates } from '../hooks/useBimWorkOrderVersionPriceStates';
import { useWorkOrderOtherVersionPriceList } from '../hooks/useWorkOrderOtherVersionPriceList';

import { useBimActsKC2VersionOther } from './hooks/useBimActsKC2VersionOther';
import { useWorkOrderOtherVersionColumns } from './hooks/useWorkOrderOtherVersionColumns';

import Style from './BimWorkOrderOthersVersionPrice.module.css';

const defaultColDef = {
    resizable: true,
    wrapHeaderText: true,
};

interface IBimWorkOrderOthersTable {
    workOrderId: number;
    workOrderVersionId: number;
    workOrderRootObjectId: number | undefined;
    constructionTypeId?: number;
    totalDetailsRowOpenOuter: boolean;
    hasInProgress: boolean;
    statusWorkOrder: WorkOrderStatusEnum;
    toggleTotalDetailsOuter: () => void;
    isFrameContract: boolean;
    refreshProject: () => void;
    resetCheckedRowsRef: React.MutableRefObject<(() => void) | undefined>;
    contractStatus?: ContractStatusEnum;
    orderRules?: IUseAccess['orderBim'];
    statusProject?: ProjectStatusEnum;
    isVersionPage?: boolean;
    onOpenActKc2Read: (act: BimActKc2VersionOtherItemData) => any;
}

const fakeRow = { rowType: BimWorkOrderRowTypeEnum.TotalsString, data: null };

const getRowId = (params: any) => params?.data?.id;

export const BimWorkOrderOthersVersionTable = forwardRef((props: IBimWorkOrderOthersTable, ref: any) => {
    const {
        workOrderId,
        workOrderRootObjectId,
        statusWorkOrder,
        constructionTypeId,
        totalDetailsRowOpenOuter,
        toggleTotalDetailsOuter,
        isFrameContract,
        resetCheckedRowsRef,
        contractStatus,
        orderRules,
        hasInProgress,
        statusProject,
        workOrderVersionId,
        isVersionPage,
        onOpenActKc2Read,
    } = props;

    const [actKc2OtherForDetails, setActKc2OtherForDetails] = useState<BimActKc2VersionOtherItemData | null>(null);

    const onActKc2OtherDetailsClose = useCallback(() => {
        setActKc2OtherForDetails(null);
    }, []);

    const {
        bimWorkOrderPriceVersionStatesData: { hasExcessCompletedOthers },
        bimWorkOrderPriceStatesMakeRequest,
    } = useBimWorkOrderVersionPriceStates(BimChapterEnum.Others, workOrderRootObjectId);

    const {
        tableItems,
        fetchedItemsCounter,
        totalItems,
        refreshWorkOrderOtherPriceList: refreshWorkOrderOtherPrice,
        changeFilterHandler,
        nameFilterItems,
    } = useWorkOrderOtherVersionPriceList({
        workOrderId,
        workOrderVersionId,
        workOrderRootObjectId,
    });

    const refreshWorkOrderOtherPriceList = useCallback(() => {
        refreshWorkOrderOtherPrice();
        bimWorkOrderPriceStatesMakeRequest();
    }, [bimWorkOrderPriceStatesMakeRequest, refreshWorkOrderOtherPrice]);

    const reInitFilter = useCallback(() => {
        (ref?.current as RefObject<AgGridReact>)?.current?.api?.destroyFilter?.('name');
    }, [ref]);

    useEffect(() => {
        reInitFilter();
    }, [nameFilterItems.length, reInitFilter]);

    const { bimActsKC2OtherData, bimActsKC2OtherMakeRequest } = useBimActsKC2VersionOther(
        workOrderVersionId,
        workOrderRootObjectId,
    );

    const menuItemsGetter = useCallback(
        (act: BimActKc2VersionOtherItemData) => [
            {
                label: 'Просмотреть реквизиты',
                onClick: () => {
                    onOpenActKc2Read(act);
                },
                groupId: 1,
                disabled: statusProject === ProjectStatusEnum.Delayed,
            },
            {
                label: 'Просмотреть итоги Акта',
                onClick: () => {
                    setActKc2OtherForDetails(act);
                },
                groupId: 1,
            },
            {
                label: 'Выгрузить Акт КС-2',
                disabled: true,
                groupId: 1,
            },
            {
                label: 'Удалить',
                disabled: true,
                groupId: 2,
            },
        ],
        [onOpenActKc2Read, statusProject],
    );

    const {
        fullSetColumns,
        detailsColumns,
        settingsColumns,
        handleTableSettingsConfirm,
        isOtherColumnVisible,
        checkedRowsArr,
        resetCheckedRows,
        closeTotalDetails,
        totalDetailsOpened,
    } = useWorkOrderOtherVersionColumns({
        refreshWorkOrderOtherPriceList,
        refreshBimActsKC2Other: bimActsKC2OtherMakeRequest,
        toggleTotalDetailsOuter,
        totalDetailsRowOpenOuter,
        constructionTypeId,
        statusWorkOrder,
        hasInProgress,
        actsKc2Other: bimActsKC2OtherData?.bimActsKC2VersionOther?.items,
        menuItemsGetter,
        isFrameContract,
        changeFilterHandler,
        nameFilterItems,
        contractStatus,
        hasExcessCompletedOthers,
        orderRules,
        statusProject,
    });

    useImperativeHandle(resetCheckedRowsRef, () => resetCheckedRows, [resetCheckedRows]);

    useImperativeHandle(
        ref,
        () => ({
            api: {
                refreshInfiniteCache: () => {
                    bimActsKC2OtherMakeRequest();
                    refreshWorkOrderOtherPriceList();
                },
            },
            ...ref,
        }),
        [bimActsKC2OtherMakeRequest, ref, refreshWorkOrderOtherPriceList],
    );

    useEffect(
        () => () => {
            resetCheckedRows();
        },
        [resetCheckedRows, workOrderId, workOrderRootObjectId],
    );

    const { rowTotalsData } = useBimWorkOrderVersionTotalsRow({
        chapter: BimChapterEnum.Others,
        constructionTypeId,
        workOrderVersionId,
        workOrderObjectId: workOrderRootObjectId,
        priceLevel: undefined,
    });

    const fakeCheckedRowForVariables = useMemo(() => {
        if (totalDetailsOpened || totalDetailsRowOpenOuter) {
            return fakeRow;
        }

        return null;
    }, [totalDetailsOpened, totalDetailsRowOpenOuter]);

    const { rowTotalsData: rowTotalDetailsData, isLoading: totalDetailsLoading } = useBimWorkOrderVersionTotalsRow({
        chapter: BimChapterEnum.Others,
        constructionTypeId,
        workOrderVersionId,
        workOrderObjectId: workOrderRootObjectId,
        useInPrices: false,
        checkedPriceRowForDetails: fakeCheckedRowForVariables,
        priceLevel: undefined,
    });

    return (
        <>
            <TableTopPanel
                checkedRowsArr={checkedRowsArr}
                resetCheckedRows={resetCheckedRows}
                totalItems={totalItems}
                fetchedItems={fetchedItemsCounter}
                optionalColumns={settingsColumns}
                handleTableSettingsConfirm={handleTableSettingsConfirm}
                isColumnVisible={isOtherColumnVisible}
                areChildrenOnLeftSide
                showPinColumn
                tableApi={ref?.current}
            />
            <div className={`ag-theme-alpine BimWorkOrderPrice BimOtherTable ${Style.bimOtherTable} customHeaderColor`}>
                <AgGridTable
                    rowData={tableItems}
                    columnDefs={fullSetColumns}
                    animateRows
                    rowSelection="multiple"
                    suppressRowClickSelection
                    overlayNoRowsTemplate="Нет данных"
                    defaultColDef={defaultColDef}
                    rowClassRules={getRowClass}
                    pinnedBottomRowData={rowTotalsData}
                    ref={ref}
                    getRowId={getRowId}
                    icons={AG_GRID_ICON_SETUP}
                    suppressDragLeaveHidesColumns
                    context={{
                        tableUniqueId: `others_${workOrderRootObjectId}`,
                    }}
                />
            </div>

            {(totalDetailsRowOpenOuter || totalDetailsOpened) && (
                <BimWorkOrderPriceRowDetails
                    modalTitle="Итоги прочих затрат"
                    data={rowTotalDetailsData}
                    columns={detailsColumns}
                    onClose={closeTotalDetails}
                    isDetailsLoading={totalDetailsLoading}
                    defaultColDef={defaultColDef}
                    styleClass="BimWorkOrderOtherPriceDetails customHeaderColor"
                    priceLevel={undefined}
                />
            )}

            {!!actKc2OtherForDetails && (
                <BimWorkOrderOtherActKc2Details
                    actKC2={actKc2OtherForDetails}
                    onClose={onActKc2OtherDetailsClose}
                    defaultColDef={defaultColDef}
                    styleClass="ActKC2Details"
                    isVersionPage={isVersionPage}
                />
            )}
        </>
    );
});

BimWorkOrderOthersVersionTable.displayName = 'BimWorkOrderOthersVersionTable';
